import { useState } from "react";
import { KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Notice, Spinner } from "@/components/common/States";
import { api, errorMessage } from "@/lib/api";

export const ProfileCard = ({ fields, description, testId = "account-profile" }) => (
  <section className="rounded-2xl border bg-card p-6 space-y-4" data-testid={testId}>
    <div className="flex items-center gap-2">
      <ShieldCheck className="h-5 w-5 text-[#0B3D91] dark:text-[#3B82F6]" aria-hidden />
      <h2 className="text-base md:text-lg font-bold">Profil akun</h2>
    </div>
    {description && <p className="text-sm text-muted-foreground">{description}</p>}
    <dl className="grid sm:grid-cols-2 gap-4">
      {fields.map(({ label, value, mono }) => (
        <div key={label} className="min-w-0">
          <dt className="text-xs text-muted-foreground">{label}</dt>
          <dd className={`font-medium break-words ${mono ? "font-mono text-sm" : ""}`}>{value || "—"}</dd>
        </div>
      ))}
    </dl>
    <p className="text-xs text-muted-foreground">Data profil dikelola oleh admin sekolah atau Pusat Kendali SIST.</p>
  </section>
);

const Field = ({ id, label, value, onChange, autoComplete, testId }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} type="password" value={value} onChange={onChange} autoComplete={autoComplete}
      maxLength={200} data-testid={testId} />
  </div>
);

export const PasswordForm = () => {
  const [form, setForm] = useState({ old: "", baru: "", ulang: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const simpan = async (e) => {
    e.preventDefault();
    if (!form.old) return setErr("Masukkan kata sandi lama Anda.");
    if (form.baru.length < 10) return setErr("Kata sandi baru minimal 10 karakter.");
    if (!/[A-Za-z]/.test(form.baru) || !/\d/.test(form.baru)) {
      return setErr("Kata sandi baru harus memuat huruf dan angka.");
    }
    if (form.baru === form.old) return setErr("Kata sandi baru harus berbeda dari kata sandi lama.");
    if (form.baru !== form.ulang) return setErr("Ulangi kata sandi baru dengan tepat sama.");
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/change-password",
        { old_password: form.old, new_password: form.baru });
      setForm({ old: "", baru: "", ulang: "" });
      toast.success(data.message);
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border bg-card p-6 max-w-xl" data-testid="account-password-panel">
      <div className="flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-[#0B3D91] dark:text-[#3B82F6]" aria-hidden />
        <h2 className="text-base md:text-lg font-bold">Reset kata sandi</h2>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        Gunakan kata sandi lama untuk memastikan perubahan dilakukan oleh pemilik akun.
      </p>
      <form onSubmit={simpan} className="space-y-4 mt-5" noValidate data-testid="change-password-form">
        <Field id="pw-lama" label="Kata sandi lama" value={form.old} onChange={set("old")}
          autoComplete="current-password" testId="change-password-old" />
        <Field id="pw-baru" label="Kata sandi baru" value={form.baru} onChange={set("baru")}
          autoComplete="new-password" testId="change-password-new" />
        <Field id="pw-ulang" label="Ketik ulang kata sandi baru" value={form.ulang} onChange={set("ulang")}
          autoComplete="new-password" testId="change-password-confirm" />
        <p className="text-xs text-muted-foreground">Minimal 10 karakter dan harus memuat huruf serta angka.</p>
        {err && <Notice tone="danger" testId="change-password-error">{err}</Notice>}
        <Button type="submit" disabled={busy} data-testid="change-password-submit">
          {busy && <Spinner />} Simpan kata sandi baru
        </Button>
      </form>
    </section>
  );
};
